/**
 * @file AddContractOrdersModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description "Add orders…" on a contract (Cam, 28 Oct 2026: "a way to add
 *              orders to any pricing contract type manually to deduct from
 *              contracted tonnage … regardless of type"). Lists the order
 *              lines of the customers the contract covers — the customer, its
 *              pricing group, or the accounts it names — whatever their
 *              product, delivering within the contract's dates (or any date).
 *              Tick lines; where the row is several contracts (a group's
 *              products, a straights contract's months) each line says which
 *              it counts against, the server's suggestion first.
 *
 *              A line that counts against something else now (its own link,
 *              or a contract through its PO) is said on the line; adding it
 *              asks first (409 CONTRACT_LINK_REPLACES), because an order line
 *              counts against one contract only.
 *
 *              1.1.0 (28 Oct 2026): milk powder prices too; each line says
 *              what it counts as in tonnes, and one that counts as typed (Units
 *              None, or no unit weight) is flagged with how to fix it.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import { formatDateShort } from '../utils/dateFormatters';
import { setQuery, targetKey, countsNowWords, defaultTargets, linksFor } from '../utils/contractOrders';
import useLatestRequest from '../hooks/useLatestRequest';
import Modal from './Modal';
import Input from './Input';
import Dropdown from './Dropdown';
import TickCheckbox from './TickCheckbox';
import QtyWithTonnes from './QtyWithTonnes';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import './ContractOrdersPanel.css';

function AddContractOrdersModal({ set, targets: givenTargets = [], onClose, onAdded }) {
  const [query, setQueryText] = useState('');
  const [allDates, setAllDates] = useState(false);
  const [data, setData] = useState({ lines: [], targets: givenTargets, ready: true, reason: null, window: null, capped: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [chosen, setChosen] = useState({});
  const [saving, setSaving] = useState(false);
  const [replaceAsk, setReplaceAsk] = useState(null);
  const latest = useLatestRequest();

  useSlideOutButton([loading, selected.size, saving]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const ticket = latest.begin();
      setLoading(true);
      setError(null);
      try {
        const qs = `${setQuery(set)}&q=${encodeURIComponent(query)}${allDates ? '&all_dates=1' : ''}`;
        const r = await fetch(`${API_URL}/api/pricing/contract-orders/candidates?${qs}`, { credentials: 'include' });
        const body = await r.json().catch(() => ({}));
        if (!latest.isLatest(ticket)) return;
        if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
        setData(body);
        setChosen(prev => ({ ...defaultTargets(body.lines), ...prev }));
      } catch (e) {
        if (latest.isLatest(ticket)) setError(e.message);
      } finally {
        if (latest.isLatest(ticket)) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, allDates]);

  const liveTargets = useMemo(() => (data.targets || []).filter(t => !t.is_draft), [data.targets]);
  const targetOptions = liveTargets.map(t => ({ value: targetKey(t), label: t.label }));
  const multi = liveTargets.length > 1;
  const notReady = data.ready === false ? data.reason : null;
  const noTarget = liveTargets.length === 0 ? 'This contract is a draft — make it live before adding orders to it.' : null;
  const block = notReady || noTarget;

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const save = async (confirmReplace = false) => {
    const links = linksFor(selected, chosen, liveTargets);
    if (!links.length) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/pricing/contract-orders/link`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ links, confirm_replace: confirmReplace })
      });
      const body = await r.json().catch(() => ({}));
      if (r.status === 409 && body.code === 'CONTRACT_LINK_REPLACES') {
        setReplaceAsk(body.replaces || []);
        return;
      }
      if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
      onAdded?.(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const windowWords = data.window && !data.window.all_dates && (data.window.from || data.window.to)
    ? `delivering ${formatDateShort(data.window.from)} – ${formatDateShort(data.window.to)}`
    : 'delivering on any date';

  const footer = (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button
          type="button"
          className={`btn-slideout${block || selected.size === 0 ? ' btn-disabled' : ''}`}
          onClick={block || selected.size === 0 ? undefined : () => save(false)}
          disabled={!!block || selected.size === 0 || saving}
          title={block || (selected.size === 0 ? 'Tick the order lines to add' : undefined)}
        >
          <div className="icon-wrapper"><span className="material-symbols-rounded">playlist_add</span></div>
          <span className="btn-label">{saving ? 'Adding…' : (selected.size ? `Add ${selected.size} order line${selected.size === 1 ? '' : 's'}` : 'Add order lines')}</span>
        </button>
      </div>
    </div>
  );

  return (
    <Modal isOpen onClose={onClose} title="Add orders to this contract" size="xl" className="contract-orders-add" footer={footer}>
      <p className="contract-orders-add-intro">
        Order lines of {liveTargets[0]?.customer || 'this customer'} and anyone this contract covers (their pricing group, or the accounts it names), any product, {windowWords}.
        Each line counts against one contract only.
      </p>
      {block && <div className="contract-orders-not-ready">{block}</div>}
      <div className="contract-orders-add-filters">
        <Input
          type="text"
          value={query}
          onChange={(e) => setQueryText(e.target.value)}
          placeholder="Search order number, product or customer…"
          icon="search"
        />
        <label className="contract-orders-add-toggle" htmlFor="contract-orders-all-dates">
          <div className="checkbox-container">
            <div className="check light">
              <input id="contract-orders-all-dates" type="checkbox" checked={allDates} onChange={(e) => setAllDates(e.target.checked)} />
              <label htmlFor="contract-orders-all-dates"></label>
            </div>
          </div>
          Any delivery date
        </label>
      </div>
      {error && <div className="linked-pos-error">{error}</div>}
      <div className="contract-orders-add-list">
        {loading && data.lines.length === 0 && <div className="contract-orders-add-empty">Loading…</div>}
        {!loading && data.lines.length === 0 && (
          <div className="contract-orders-add-empty">No order lines to add{query ? ' match the search' : ''}{allDates ? '' : ' in the contract\'s dates — try Any delivery date'}.</div>
        )}
        {data.lines.length > 0 && (
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}></th>
                <th>Order</th>
                <th>Customer</th>
                <th>Delivery</th>
                <th>Product</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                {multi && <th>Count against</th>}
                <th>Counts against now</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map(l => {
                const isSel = selected.has(l.order_line_id);
                const now = countsNowWords(l);
                return (
                  <tr key={l.order_line_id} className={isSel ? 'is-selected' : undefined} onClick={() => toggle(l.order_line_id)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <TickCheckbox
                        id={`contract-order-${l.order_line_id}`}
                        checked={isSel}
                        onChange={() => toggle(l.order_line_id)}
                        ariaLabel={`Add ${l.order_number}`}
                      />
                    </td>
                    <td className="nowrap"><strong>{l.order_number}</strong>{l.invoiced && <span className="contract-orders-note">Invoiced</span>}</td>
                    <td>{l.account_no ? `${l.account_no} · ` : ''}{l.customer}</td>
                    <td className="nowrap">{formatDateShort(l.delivery_date)}</td>
                    <td>{l.item_code ? `${l.item_code} ` : ''}{l.product_name}</td>
                    <td style={{ textAlign: 'right' }}>
                      <QtyWithTonnes quantity={l.quantity} line={l} />
                      {l.counts_flag && <span className="contract-orders-note is-warning">{l.counts_flag}</span>}
                    </td>
                    {multi && (
                      <td className="contract-orders-add-target" onClick={(e) => e.stopPropagation()}>
                        <Dropdown
                          compact
                          escapeOverflow
                          options={targetOptions}
                          value={chosen[l.order_line_id] || ''}
                          onChange={(v) => setChosen(prev => ({ ...prev, [l.order_line_id]: v }))}
                          placeholder="Choose…"
                        />
                      </td>
                    )}
                    <td className={now ? undefined : 'refresh-auto-links-muted'}>{now || 'Nothing'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {data.capped && <div className="contract-orders-add-capped">Showing the first 300 — search to narrow the list.</div>}

      <ConfirmDialog
        isOpen={!!replaceAsk}
        type="warning"
        title="Move these order lines to this contract?"
        className="refresh-auto-links"
        message={replaceAsk ? (
          <div className="refresh-auto-links-body">
            <p>An order line counts against one contract only. Adding {replaceAsk.length === 1 ? 'this line' : 'these lines'} here takes {replaceAsk.length === 1 ? 'it' : 'them'} off where {replaceAsk.length === 1 ? 'it counts' : 'they count'} now:</p>
            <ul>{replaceAsk.map(r => <li key={r.order_line_id}>{r.text}</li>)}</ul>
          </div>
        ) : ''}
        confirmText="Move them here"
        confirmIcon="swap_horiz"
        onConfirm={() => { setReplaceAsk(null); save(true); }}
        onCancel={() => setReplaceAsk(null)}
      />
    </Modal>
  );
}

export default AddContractOrdersModal;
